// ServiciosPage.js
import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, AppBar, Toolbar, Button, Box,
    CircularProgress, IconButton, styled
} from "@mui/material";

import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faBan, faCheck } from "@fortawesome/free-solid-svg-icons";

import ModalServicio from "../components/ModalServicio";
import ModalDetalleServicio from "../components/ModalDetalleServicio";

const API_URL = process.env.REACT_APP_API_URL || "https://api.hmsaa.com.mx:5001";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold"
}));

export default function ServiciosPage() {
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(true);

    const [openModal, setOpenModal] = useState(false);
    const [servicioDetalle, setServicioDetalle] = useState(null);

    const defaultServicio = {
        nombre: "",
        descripcion: "",
        id_proveedor: "",
        xml_concepto_id: "",
        precio: "",
    };

    const [newServicio, setNewServicio] = useState(defaultServicio);

    const token = localStorage.getItem("access_token");

    const headers = {
        Authorization: `Bearer ${token}`
    };

    // ===============================
    // CARGA INICIAL
    // ===============================
    const fetchServicios = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/servicios`, { headers });
            const data = await res.json();
            setServicios(data || []);
        } catch {
            Swal.fire("Error", "No se pudieron cargar los servicios", "error");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchServicios();
    }, []);

    // ===============================
    // HABILITAR / DESHABILITAR
    // ===============================
    const toggleDisable = async (servicio) => {
        const nuevoEstado = !servicio.isDisable;

        const confirm = await Swal.fire({
            title: "¿Cambiar estado?",
            text: servicio.nombre,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí"
        });

        if (!confirm.isConfirmed) return;

        await fetch(`${API_URL}/api/servicios/${servicio.id}/status`, {
            method: "PUT",
            headers: {
                ...headers,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ isDisable: nuevoEstado })
        });

        fetchServicios();
    };

    // ===============================
    // GUARDAR
    // ===============================
    const handleSaveServicio = async () => {
        try {
            const formData = new FormData();

            Object.entries(newServicio).forEach(([k, v]) => {
                if (v === undefined || v === null) return;
                if (typeof v === "string" && (v.trim() === "" || v === "undefined")) return;
                formData.append(k, v);
            });

            const res = await fetch(`${API_URL}/api/servicios`, {
                method: "POST",
                headers,
                body: formData
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            Swal.fire("OK", "Servicio creado", "success");
            setOpenModal(false);
            setNewServicio(defaultServicio);
            fetchServicios();
        } catch (e) {
            Swal.fire("Error", e.message || "No se pudo crear", "error");
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={5}>
                <CircularProgress />
                <Typography>Cargando...</Typography>
            </Box>
        );
    }

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography sx={{ flexGrow: 1 }} variant="h6">
                        Servicios
                    </Typography>

                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => {
                            setNewServicio(defaultServicio);
                            setOpenModal(true);
                        }}
                    >
                        Nuevo Servicio
                    </Button>
                </Toolbar>
            </AppBar>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Nombre</StyledTableCell>
                            <StyledTableCell>Descripción</StyledTableCell>
                            <StyledTableCell>Precio</StyledTableCell>
                            <StyledTableCell>Proveedor</StyledTableCell>
                            <StyledTableCell>Estado</StyledTableCell>
                            <StyledTableCell align="center">Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {servicios.map(s => (
                            <TableRow
                                key={s.id}
                                hover
                                sx={{ opacity: s.isDisable ? 0.4 : 1 }}
                                onClick={() => setServicioDetalle(s)}
                            >
                                <TableCell>{s.nombre}</TableCell>
                                <TableCell>{s.descripcion}</TableCell>
                                <TableCell>${s.precio}</TableCell>
                                <TableCell>{s.proveedor}</TableCell>
                                <TableCell>{s.isDisable ? "Inactivo" : "Activo"}</TableCell>

                                <TableCell
                                    align="center"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <IconButton onClick={() => toggleDisable(s)}>
                                        <FontAwesomeIcon
                                            icon={s.isDisable ? faCheck : faBan}
                                            color={s.isDisable ? "#2e7d32" : "#d32f2f"}
                                        />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {openModal && (
                <ModalServicio
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    newServicio={newServicio}
                    setNewServicio={setNewServicio}
                    handleSaveServicio={handleSaveServicio}
                />
            )}

            {servicioDetalle && (
                <ModalDetalleServicio
                    open={!!servicioDetalle}
                    servicio={servicioDetalle}
                    onClose={() => setServicioDetalle(null)}
                />
            )}
        </>
    );
}
